import {
  AnimationClip,
  type KeyframeTrack,
  MathUtils,
  Quaternion,
  QuaternionKeyframeTrack,
  type Skeleton,
  Vector3,
  VectorKeyframeTrack
} from 'three'

/**
 * Procedurally builds an `AnimationClip` from a declarative JSON "animation spec"
 * and a live `Skeleton`. This is the engine behind the `generate-animation` skill:
 * a natural-language description is translated (offline, by the author) into a
 * spec, and this class realizes it as keyframe tracks against whatever rig is
 * currently loaded.
 *
 * Conventions (shared with the skill's bone catalogs):
 *   - Bones point along their LOCAL +Y axis toward their child (Blender export
 *     convention). So rotating about local X = pitch/bend, local Z = roll/spread,
 *     local Y = twist along the bone.
 *   - Each track's rotation is authored as a DELTA applied in the bone's body
 *     frame: `local = bind_local * delta`. The bind/base orientation is read from
 *     the bone's current local transform, so the caller must put the skeleton in
 *     its bind pose first (e.g. `skeleton.pose()`).
 *   - Position is only meaningful for the rig's root-motion bone; position values
 *     are deltas (meters) added to the bone's bind translation.
 *
 * The output uses standard `<bone>.<property>` track names, so the clip drops
 * straight into the existing playback/export/retarget pipeline.
 */

export type Easing = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out'
export type SpecAxis = 'x' | 'y' | 'z'
export type SpecChannel = 'rotation' | 'position'

export interface SpecKeyPose {
  /** Time in seconds (clamped to [0, duration]). */
  t: number
  /** Rotation: euler degrees [x, y, z]. Position: delta meters [x, y, z]. */
  value: [number, number, number]
  /** Easing of the segment ENDING at this key. Defaults to 'ease-in-out'. */
  ease?: Easing
}

export interface AnimationSpecTrack {
  bone: string
  mode: 'oscillator' | 'keyposes'
  /** Defaults to 'rotation'. */
  channel?: SpecChannel

  // --- oscillator mode ---
  axis?: SpecAxis
  /** Degrees (rotation) or meters (position). */
  amplitude?: number
  frequency_hz?: number
  phase_deg?: number
  /** Constant added to the oscillation (degrees or meters). */
  offset?: number

  // --- keyposes mode ---
  keys?: SpecKeyPose[]
}

export interface AnimationSpec {
  name: string
  /** Informational; not required to match the live skeleton. */
  skeleton_type?: string
  duration: number
  fps?: number
  loop?: boolean
  /** Bone allowed to carry a position (root-motion) track, e.g. 'pelvis'. */
  root_motion_bone?: string
  tracks: AnimationSpecTrack[]
}

export interface GenerateResult {
  clip: AnimationClip
  warnings: string[]
}

export class ProceduralClipGenerator {
  private static readonly DEFAULT_FPS = 30
  private static readonly DEFAULT_EASE: Easing = 'ease-in-out'

  /**
   * Realize `spec` against `skeleton`. Reads each driven bone's current local
   * transform as the bind/base — call `skeleton.pose()` beforehand to guarantee
   * the bind pose. Bones referenced by the spec but missing from the skeleton are
   * skipped (recorded in `warnings`) so one spec can degrade gracefully across rigs.
   */
  static generate (spec: AnimationSpec, skeleton: Skeleton): GenerateResult {
    const warnings: string[] = []
    const duration = Math.max(spec.duration, 1e-3)
    const fps = spec.fps !== undefined && spec.fps > 0 ? spec.fps : this.DEFAULT_FPS
    const times = this.build_times(duration, fps)

    const bone_by_name = new Map(skeleton.bones.map((b) => [b.name, b]))
    const tracks: KeyframeTrack[] = []

    for (const track_spec of spec.tracks) {
      const bone = bone_by_name.get(track_spec.bone)
      if (bone === undefined) {
        warnings.push(`bone "${track_spec.bone}" not found on skeleton — track skipped`)
        continue
      }

      const channel: SpecChannel = track_spec.channel ?? 'rotation'

      if (channel === 'position') {
        if (track_spec.bone !== spec.root_motion_bone) {
          warnings.push(`position track on "${track_spec.bone}" ignored — only the root-motion bone (${spec.root_motion_bone ?? 'none'}) may translate`)
          continue
        }
        tracks.push(this.build_position_track(track_spec, times, bone.position))
      } else {
        tracks.push(this.build_rotation_track(track_spec, times, bone.quaternion, warnings))
      }
    }

    return { clip: new AnimationClip(spec.name, duration, tracks), warnings }
  }

  /** Inclusive sample times [0 .. duration], so a looping clip closes cleanly. */
  private static build_times (duration: number, fps: number): number[] {
    const frame_count = Math.max(1, Math.round(duration * fps))
    const times: number[] = []
    for (let i = 0; i <= frame_count; i++) {
      times.push((i / frame_count) * duration)
    }
    return times
  }

  private static build_rotation_track (
    spec: AnimationSpecTrack,
    times: number[],
    base_quat: Quaternion,
    warnings: string[]
  ): QuaternionKeyframeTrack {
    const base = base_quat.clone()
    const delta = new Quaternion()
    const result = new Quaternion()
    const values: number[] = []

    for (const t of times) {
      this.rotation_delta_at(spec, t, times[times.length - 1], delta, warnings)
      // body-frame compose: bind * delta
      result.copy(base).multiply(delta)
      values.push(result.x, result.y, result.z, result.w)
    }

    return new QuaternionKeyframeTrack(`${spec.bone}.quaternion`, [...times], values)
  }

  private static build_position_track (
    spec: AnimationSpecTrack,
    times: number[],
    base_pos: Vector3
  ): VectorKeyframeTrack {
    const base = base_pos.clone()
    const delta = new Vector3()
    const values: number[] = []

    for (const t of times) {
      this.vector_delta_at(spec, t, times[times.length - 1], delta)
      values.push(base.x + delta.x, base.y + delta.y, base.z + delta.z)
    }

    return new VectorKeyframeTrack(`${spec.bone}.position`, [...times], values)
  }

  /** Writes the rotation delta quaternion for time `t` into `out`. */
  private static rotation_delta_at (
    spec: AnimationSpecTrack,
    t: number,
    duration: number,
    out: Quaternion,
    warnings: string[]
  ): void {
    if (spec.mode === 'oscillator') {
      const angle_deg = this.oscillator_value(spec, t)
      out.setFromAxisAngle(this.axis_vector(spec.axis ?? 'x'), MathUtils.degToRad(angle_deg))
      return
    }

    // keyposes: slerp between bracketing key delta-quaternions.
    const euler = this.interp_keyposes(spec, t, duration, warnings)
    this.euler_deg_to_quat(euler, out)
  }

  /** Writes the position delta vector for time `t` into `out`. */
  private static vector_delta_at (spec: AnimationSpecTrack, t: number, duration: number, out: Vector3): void {
    if (spec.mode === 'oscillator') {
      const v = this.oscillator_value(spec, t)
      const axis = spec.axis ?? 'y'
      out.set(axis === 'x' ? v : 0, axis === 'y' ? v : 0, axis === 'z' ? v : 0)
      return
    }
    const value = this.interp_keyposes(spec, t, duration, [])
    out.set(value[0], value[1], value[2])
  }

  private static oscillator_value (spec: AnimationSpecTrack, t: number): number {
    const amplitude = spec.amplitude ?? 0
    const frequency = spec.frequency_hz ?? 1
    const phase = MathUtils.degToRad(spec.phase_deg ?? 0)
    const offset = spec.offset ?? 0
    return offset + amplitude * Math.sin(2 * Math.PI * frequency * t + phase)
  }

  /**
   * Linear/eased interpolation across a sorted keypose list. For rotation the
   * returned triple is euler degrees; for position it is a delta vector. Times
   * outside the key range clamp to the nearest key.
   */
  private static interp_keyposes (
    spec: AnimationSpecTrack,
    t: number,
    duration: number,
    warnings: string[]
  ): [number, number, number] {
    const keys = spec.keys ?? []
    if (keys.length === 0) {
      warnings.push(`keyposes track on "${spec.bone}" has no keys — using zero`)
      return [0, 0, 0]
    }

    const sorted = [...keys].sort((a, b) => a.t - b.t)
    if (t <= sorted[0].t) return sorted[0].value
    const last = sorted[sorted.length - 1]
    if (t >= last.t) return last.value

    let i = 0
    while (i < sorted.length - 1 && sorted[i + 1].t < t) i++
    const a = sorted[i]
    const b = sorted[i + 1]
    const span = b.t - a.t
    const u = span > 1e-9 ? (t - a.t) / span : 0
    const eased = this.apply_easing(u, b.ease ?? this.DEFAULT_EASE)

    return [
      a.value[0] + (b.value[0] - a.value[0]) * eased,
      a.value[1] + (b.value[1] - a.value[1]) * eased,
      a.value[2] + (b.value[2] - a.value[2]) * eased
    ]
  }

  private static apply_easing (u: number, ease: Easing): number {
    const x = Math.max(0, Math.min(1, u))
    switch (ease) {
      case 'linear': return x
      case 'ease-in': return x * x
      case 'ease-out': return 1 - (1 - x) * (1 - x)
      case 'ease-in-out': return x * x * (3 - 2 * x)
      default: return x
    }
  }

  private static euler_deg_to_quat (euler_deg: [number, number, number], out: Quaternion): void {
    const qx = new Quaternion().setFromAxisAngle(this.AXIS_X, MathUtils.degToRad(euler_deg[0]))
    const qy = new Quaternion().setFromAxisAngle(this.AXIS_Y, MathUtils.degToRad(euler_deg[1]))
    const qz = new Quaternion().setFromAxisAngle(this.AXIS_Z, MathUtils.degToRad(euler_deg[2]))
    // XYZ order
    out.copy(qx).multiply(qy).multiply(qz)
  }

  private static readonly AXIS_X = new Vector3(1, 0, 0)
  private static readonly AXIS_Y = new Vector3(0, 1, 0)
  private static readonly AXIS_Z = new Vector3(0, 0, 1)

  private static axis_vector (axis: SpecAxis): Vector3 {
    switch (axis) {
      case 'x': return this.AXIS_X
      case 'y': return this.AXIS_Y
      case 'z': return this.AXIS_Z
      default: return this.AXIS_X
    }
  }
}
